import type { TeammateTopic, TopicTeammate, TrendTopic } from '@mg/shared';

/** How topics are compared — trimmed and case-insensitive, exactly as the API matches them. */
export function topicKey(phrase: string): string {
  return phrase.trim().toLowerCase();
}

/** How many avatars a teammate chip stacks before it relies on the count alone. */
export const MAX_STACKED_AVATARS = 3;

/** The teammates whose avatars a chip shows: the first three. The count beside them says the rest. */
export function stackedTeammates<T>(users: T[]): T[] {
  return users.slice(0, MAX_STACKED_AVATARS);
}

/**
 * Teammates' topics the user does not have yet. The API already leaves out the user's own topics;
 * filtering again here is what makes a chip disappear the moment its topic lands in the list. When
 * × takes it out again, the panel puts the chip back into the teammates list itself
 * ({@link withTeammateTopic}), since a refetch in between may have dropped it.
 */
export function pendingTeammateTopics(
  teammates: TeammateTopic[],
  mine: Pick<TrendTopic, 'phrase'>[],
): TeammateTopic[] {
  const have = new Set(mine.map((t) => topicKey(t.phrase)));
  return teammates.filter((t) => !have.has(topicKey(t.label)));
}

/**
 * The order the API returns teammate topics in: most teammates first, then A to Z ignoring case,
 * with code-point order to settle labels that differ only in case.
 */
export function compareTeammateTopics(a: TeammateTopic, b: TeammateTopic): number {
  if (a.users.length !== b.users.length) return b.users.length - a.users.length;
  const x = a.label.toLowerCase();
  const y = b.label.toLowerCase();
  if (x !== y) return x < y ? -1 : 1;
  return a.label < b.label ? -1 : a.label > b.label ? 1 : 0;
}

/** `list` with `topic` in its place — unless a topic with the same key is already there. */
export function withTeammateTopic(list: TeammateTopic[], topic: TeammateTopic): TeammateTopic[] {
  const key = topicKey(topic.label);
  if (list.some((t) => topicKey(t.label) === key)) return list;
  return [...list, topic].sort(compareTeammateTopics);
}

export type SuggestionView = 'team' | 'ai';

/**
 * Which suggestions to show. Teammates' by default whenever there are any; once there are none the
 * tab goes away, so AI suggestions it is no matter what was picked before.
 */
export function suggestionView(chosen: SuggestionView | null, teammateCount: number): SuggestionView {
  if (teammateCount === 0) return 'ai';
  return chosen ?? 'team';
}

/** "Used by 3 teammates: Priya Nair, Jordan Rivera, Sam Okafor" — the avatar button's name. */
export function usedByLabel(users: Pick<TopicTeammate, 'name'>[]): string {
  const noun = users.length === 1 ? 'teammate' : 'teammates';
  return `Used by ${users.length} ${noun}: ${users.map((u) => u.name).join(', ')}`;
}
